import { useCallback, useContext, useMemo, useState } from 'react';
import type React from 'react';
import { type ChatInteractionMode } from '@trinity/chat/enums';
import type { TTSConfig } from '@trinity/chat/src/types/tts-config';
import { ChatInstance } from '@trinity/chat-components/components/Chat/Chat.js';
import { ChatSettingsToggleContext } from '@trinity/chat-components/components/Chat/context.js';
import { ChatHeader } from '@trinity/chat-components/components/Chat.Header';
import { ChatSettings } from '@trinity/chat-components/components/Chat.Screen/Chat.Setting.js';
import { ChatSidebar } from '@trinity/chat-components/components/Chat.Screen/Sidebar.js';
import { useHasChatVoiceModeEnabled } from '@trinity/chat-components/containers/Chat/selectors';
import { HeyGenResponseBridge } from '../LiveAvatar/HeyGenResponseBridge';
import { LiveAvatar } from '../LiveAvatar/LiveAvatar';
import { LiveAvatarSessionManager } from '../LiveAvatar/LiveAvatarSessionManager';
import { AvatarPanelContext, reduceAvatarPanelContext } from './AvatarPanel.context';
import { ChatPanel, GalleryPanel, Root, SidebarWrapper } from './DigitalTwinChat.styles';
import { HeyGenPanel } from './HeyGenPanel';
import { useIsDigitalTwinLoading } from './useIsDigitalTwinLoading';
import { DigitalTwinLoader } from './DigitalTwinLoader';
import { SegmentationDashboardContext } from '../../containers/Segmentation.Dashboard/context';

type Props = {
  avatarId?: string;
  ttsConfig: TTSConfig | null;
  galleryContent?: React.ReactNode;
  disableVideoMode?: boolean;
  segmentIconUrl?: string;
  hideHeader?: boolean;
};

/**
 * The inner screen component — consumes contexts provided by the stack above.
 * Separated so hooks run inside the provider tree.
 *
 * When avatarId + voiceId are provided:
 *   - LiveAvatarSessionManager wraps the entire layout, fetches the session
 *     token, and provides LiveAvatarContext to all descendants.
 *   - HeyGenPanel receives <LiveAvatar /> which attaches the video stream.
 *   - HeyGenResponseBridge (renderless) listens for completed AI responses
 *     and will drive avatar speech via repeatAudio once TTS is ready.
 *
 * Without avatar config, HeyGenPanel shows its placeholder and the chat
 * functions as a standard text-only conversation.
 */
export const DigitalTwinScreen = ({ avatarId, ttsConfig, galleryContent, disableVideoMode, segmentIconUrl: segmentIconUrlProp, hideHeader }: Props) => {
  const [settings] = useContext(ChatSettingsToggleContext);
  const isLoading = useIsDigitalTwinLoading();

  const [sessionKey, setSessionKey] = useState(0);
  const segmentIconUrlFromContext = useContext(SegmentationDashboardContext).segment?.iconUrl;
  const segmentIconUrl = segmentIconUrlProp ?? segmentIconUrlFromContext;
  const [avatarPanelCtx, expanded, setExpanded, interactionMode] = useProvideAvatarPanelContext(segmentIconUrl, disableVideoMode);

  const handleToggleExpand = () => setExpanded((prev) => !prev);
  const handleReinitialize = useCallback(() => setSessionKey((k) => k + 1), []);

  if (isLoading && !galleryContent) {
    return <DigitalTwinLoader />;
  }

  if (galleryContent) {
    return (
      <AvatarPanelContext.Provider value={avatarPanelCtx}>
        <Root videoMode={false} expanded={false}>
          <SidebarWrapper>
            <ChatSidebar />
          </SidebarWrapper>
          <GalleryPanel>
            {galleryContent}
          </GalleryPanel>
        </Root>
      </AvatarPanelContext.Provider>
    );
  }

  // The HeyGen session is active only in Video mode.
  // Voice mode uses the existing speech-recognition pipeline (VoiceModeContext)
  // and does NOT start a HeyGen session or feed responses to the avatar.
  // Text mode is plain chat with no avatar or TTS.
  // Mounting/unmounting LiveAvatarSessionManager is what starts and stops the
  // session -- its useEffect cleanup handles teardown.
  const sessionActive = !!avatarId && interactionMode === 'video';

  const layout = (
    <Root videoMode={interactionMode === 'video'} expanded={expanded}>
      {/* Column 1: Chat history sidebar */}
      <SidebarWrapper>
        <ChatSidebar />
      </SidebarWrapper>
      {/* Column 2: HeyGen avatar panel — omitted in text/voice mode so ChatPanel
          fills full width, matching the Insights Chat full-screen layout. */}
      {interactionMode === 'video' && (
        <HeyGenPanel
          expanded={expanded}
          onToggleExpand={handleToggleExpand}
          sessionActive={sessionActive}
          onReinitialize={handleReinitialize}
        >
          {sessionActive && <LiveAvatar />}
        </HeyGenPanel>
      )}
      {/* Column 3 (or full width in text/voice mode): Chat input + message list */}
      <ChatPanel expanded={expanded}>
        {!settings && !hideHeader && <ChatHeader />}
        {settings ? <ChatSettings /> : <ChatInstance />}
        {/* Renderless - bridges AI responses to avatar speech */}
        {sessionActive && <HeyGenResponseBridge />}
      </ChatPanel>
    </Root>
  );

  return (
    <AvatarPanelContext.Provider value={avatarPanelCtx}>
      {sessionActive ? (
        <LiveAvatarSessionManager key={sessionKey} avatarId={avatarId} ttsConfig={ttsConfig}>
          {layout}
        </LiveAvatarSessionManager>
      ) : (
        layout
      )}
    </AvatarPanelContext.Provider>
  );
};

function useProvideAvatarPanelContext(segmentIconUrl: string | undefined, disableVideoMode?: boolean) {
  const [expanded, setExpanded] = useState(false);
  const hasVideoModeFeature = !disableVideoMode;
  const hasVoiceModeFeature = useHasChatVoiceModeEnabled();

  const defaultInitialMode: ChatInteractionMode = hasVideoModeFeature ? 'video' : 'text';

  const [interactionMode, setInteractionMode] = useState<ChatInteractionMode>(defaultInitialMode);

  const ctx = useMemo(
    () =>
      reduceAvatarPanelContext({
        expanded,
        setExpanded,
        isDigitalTwin: true,
        isNewChatInteractionLayout: true,
        availableModes: [
          hasVideoModeFeature ? 'video' : undefined,
          hasVoiceModeFeature ? 'voice' : undefined,
          'text',
        ].filter((m): m is ChatInteractionMode => !!m),
        defaultInteractionMode: defaultInitialMode,
        interactionMode,
        setInteractionMode,
        segmentIconUrl,
        showAuthor: interactionMode !== 'video',
      }),
    [expanded, hasVideoModeFeature, hasVoiceModeFeature, defaultInitialMode, interactionMode, segmentIconUrl]
  );

  return [ctx, expanded, setExpanded, interactionMode] as const;
}
